import React from "react"
import types from "prop-types"
import { isEqual } from "lodash-es"
import { useDispatch, useSelector } from "react-redux"
import { subscribedToAddOnFeature } from "reduxSlices/addOnFeatureSubscriptionsSlice"
import {
  getFormQuestionAttributeUserTypes,
  setQuestionAttributeUserTypes,
} from "reduxSlices/formBuilderSlice"
import { AUTHENTICATED_FORMS_FEATURE_NAME } from "utils/addOnFeatureHelpers"
import {
  ACTIVITY_HUB_USER_TYPE,
  READABLE_USER_TYPE_NAMES,
  SUBMITTER_USER_TYPE,
} from "utils/userRoleHelpers"
import { Checkbox } from "shared/checkboxes"
import UserTypesSelectedValidation from "./UserTypesSelectedValidation"

const USER_TYPES = [ACTIVITY_HUB_USER_TYPE, SUBMITTER_USER_TYPE]

const UserTypes = ({ sectionIndex, questionIndex }) => {
  const dispatch = useDispatch()

  // A question built before the userTypes attribute existed has no attribute to read, so the
  // selector builds the default user types.  isEqual keeps that newly-built array from being
  // seen as a new value on every store update.
  const userTypes = useSelector(getFormQuestionAttributeUserTypes({ sectionIndex, questionIndex }), isEqual)

  const isSubscribedToAuthenticatedForms = useSelector(subscribedToAddOnFeature(AUTHENTICATED_FORMS_FEATURE_NAME))

  // An organization not subscribed to the Authenticated Forms feature has no submitters, leaving
  // Activity Hub users as the only user type and nothing for the form builder to choose between.
  if (!isSubscribedToAuthenticatedForms) return null

  const updateUserTypes = (newUserTypes) => dispatch(setQuestionAttributeUserTypes({
    sectionIndex, questionIndex, newUserTypes,
  }))

  // The user types are kept in a consistent order, regardless of the order
  // in which they were selected.
  const toggleUserType = (userType) => (selected) => {
    const newUserTypes = selected
      ? USER_TYPES.filter((type) => type === userType || userTypes.includes(type))
      : userTypes.filter((type) => type !== userType)

    updateUserTypes(newUserTypes)
  }

  return (
    <div className="mb-4">
      <div className="mb-2 font-medium">Answer Options</div>
      {
        USER_TYPES.map((userType) => {
          const checkboxId = `section-${sectionIndex}-question-${questionIndex}-user-type-${userType}`

          return (
            <div key={userType} className="mb-1 flex items-center gap-2">
              <Checkbox
                uuid={checkboxId}
                value={userTypes.includes(userType)}
                onChange={toggleUserType(userType)}
              />
              <label htmlFor={checkboxId}>
                {READABLE_USER_TYPE_NAMES[userType]}
              </label>
            </div>
          )
        })
      }
      <UserTypesSelectedValidation
        value={userTypes}
        sectionIndex={sectionIndex}
        questionIndex={questionIndex}
      />
    </div>
  )
}

UserTypes.propTypes = {
  sectionIndex: types.number.isRequired,
  questionIndex: types.number.isRequired,
}

export default UserTypes
